// src/pages/negative/NegativeCreatePage.jsx — React Hook Form + React Query + Pré-check Indireto (completo)
import React from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Label,
  TextInput,
  Select,
  Alert,
  Modal,
  Checkbox,
} from "flowbite-react";
import {
  HiLightningBolt,
  HiInformationCircle,
  HiCheckCircle,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { ADMIN, COMPANY, NEGATIVE } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { useForm, Controller, useWatch } from "react-hook-form";
import { formatDocument } from "../../utils/utils";
import DrawerLoading from "../DrawerLoading";

/* ===========================
 * Constantes / Opções
 * =========================== */
const NATUREZAS = [
  { Codigo: 60, Sigla: "CI", Descricao: "CÉDULA DE CRÉDITO INDUSTRIAL" },
  { Codigo: 13, Sigla: "CR", Descricao: "CÉDULA RURAL PIGNORATÍCIA" },
  { Codigo: 59, Sigla: "CH", Descricao: "CHEQUE" },
  { Codigo: 6, Sigla: "CB", Descricao: "CONTRATO DE CÂMBIO" },
  { Codigo: 61, Sigla: "CS", Descricao: "CONTRATOS DIVERSOS" },
  { Codigo: 8, Sigla: "CD", Descricao: "CREDIÁRIO" },
  { Codigo: 12, Sigla: "CP", Descricao: "CRÉDITO PESSOAL" },
  { Codigo: 62, Sigla: "DB", Descricao: "DEBENTURES" },
  { Codigo: 24, Sigla: "DP", Descricao: "DUPLICATA" },
  { Codigo: 63, Sigla: "LC", Descricao: "LETRA DE CÂMBIO" },
  { Codigo: 64, Sigla: "NE", Descricao: "NOTA CRÉDITO EXPORTAÇÃO" },
  { Codigo: 65, Sigla: "NP", Descricao: "NOTA PROMISSÓRIA" },
  { Codigo: 66, Sigla: "XX", Descricao: "OUTRAS OPERAÇÕES" },
];

const UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

/* ===========================
 * Helpers / máscaras / validações
 * =========================== */
function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(date, delta) {
  const d = new Date(date);
  d.setDate(d.getDate() + delta);
  return d;
}
function addMonths(date, delta) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + delta);
  if (d.getDate() < day) d.setDate(0);
  return d;
}
const CONTRATO_ALLOWED = /^[0-9A-Z/\-.]{1,16}$/;
const NATUREZA_DC = "22";
function isAdult(dateStr) {
  if (!dateStr) return true;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const limit = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
  return d <= limit;
}
async function lookupCEP(cepDigits) {
  const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
  if (!res.ok) throw new Error("Falha ao consultar CEP");
  const data = await res.json();
  if (data.erro) throw new Error("CEP não encontrado");
  return data;
}
function formatBRLMask(input) {
  const digits = onlyDigits(input);
  if (!digits) return "";
  const cents = parseInt(digits, 10);
  const int = Math.floor(cents / 100);
  const frac = String(cents % 100).padStart(2, "0");
  const intFmt = int.toLocaleString("pt-BR");
  return `${intFmt},${frac}`;
}
function parseBRLToNumber(masked) {
  if (!masked) return 0;
  const s = String(masked).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}
const onlyDigits = (v) => (v == null ? "" : String(v)).replace(/\D+/g, "");
const maskCNPJorCPF = (v) => {
  const d = onlyDigits(v);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4")
      .slice(0, 14);
  }
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5")
    .slice(0, 18);
};
const maskCNPJ = (v) =>
  onlyDigits(v)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5")
    .slice(0, 18);
const maskCEP = (v) =>
  onlyDigits(v)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2-$3")
    .slice(0, 10);
const maskPhoneWithDDD = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  const ddd = d.slice(0, 2);
  const tel = d.slice(2);
  if (!ddd && !tel) return "";
  const left =
    tel.length > 5 ? `${tel.slice(0, tel.length - 4)}-${tel.slice(-4)}` : tel;
  return ddd ? `(${ddd}) ${left}` : left;
};
function splitDDDPhone(v) {
  const d = onlyDigits(v).slice(0, 11);
  return { ddd: d.slice(0, 2), telefone: d.slice(2) };
}
const maskUF = (v) => (v || "").toUpperCase().slice(0, 2);
// ===== Validações CPF/CNPJ =====
function isValidCPF(str) {
  const cpf = onlyDigits(str).padStart(11, "0");
  if (!cpf || cpf.length !== 11) return false;
  if (new Set(cpf).size === 1) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i), 10) * (10 - i);
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9), 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i), 10) * (11 - i);
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  return rev === parseInt(cpf.charAt(10), 10);
}
function isValidCNPJ(str) {
  const cnpj = onlyDigits(str).padStart(14, "0");
  if (!cnpj || cnpj.length !== 14) return false;
  if (new Set(cnpj).size === 1) return false;
  const calc = (base) => {
    const weights =
      base === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const slice = base === 12 ? cnpj.slice(0, 12) : cnpj.slice(0, 13);
    const n = slice
      .split("")
      .reduce((acc, digit, idx) => acc + parseInt(digit, 10) * weights[idx], 0);
    const r = n % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = calc(12);
  if (d1 !== parseInt(cnpj.charAt(12), 10)) return false;
  const d2 = calc(13);
  return d2 === parseInt(cnpj.charAt(13), 10);
}

/* ===========================
 * Componente
 * =========================== */
export default function NegativeCreatePage() {
  const { user } = useApplication();
  const navigate = useNavigate();

  // Janela válida: [hoje - 4 anos e 11 meses,  hoje - 4 dias]
  const vencMinDate = React.useMemo(() => addMonths(new Date(), -59), []);
  const vencMaxDate = React.useMemo(() => addDays(new Date(), -5), []);
  const vencMin = React.useMemo(() => toYMD(vencMinDate), [vencMinDate]);
  const vencMax = React.useMemo(() => toYMD(vencMaxDate), [vencMaxDate]);

  // ===== Pré-check Indireto =====
  const [checkingIndireto, setCheckingIndireto] = React.useState(true);
  const [showIndiretoModal, setShowIndiretoModal] = React.useState(false);
  const [indiretoError, setIndiretoError] = React.useState("");

  // RHF do modal de cadastro indireto (form separado)
  const {
    control: controlInd,
    handleSubmit: handleSubmitInd,
    setValue: setValueInd,
    formState: { errors: errorsInd, isSubmitting: isSubmittingInd },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      Usuario: "",
      Senha: "",
      Cnpj: "",
      CnpjIndireto: "",
      EmailIndireto: "",
      SenhaIndireto: "",
      Nome: "",
      NomeFantasia: "",
      Telefone: "",
      Cep: "",
      Endereco: "",
      Numero: "",
      Complemento: "",
      Bairro: "",
      Cidade: "",
      DataAbertura: "",
      Cnae: "",
    },
  });

  // Prefill com dados do Company quando o user carregar
  React.useEffect(() => {
    if (!user?.Company) return;
    const c = user.Company;
    setValueInd("Cnpj", onlyDigits(c.document || ""));
    setValueInd("CnpjIndireto", onlyDigits(c.document || ""));
    setValueInd("EmailIndireto", (c.email || "").trim());
    setValueInd("Nome", c.socialReason || c.name || "");
    setValueInd("Telefone", onlyDigits(c.phoneNumber || c.phone || ""));
    setValueInd("Cep", onlyDigits(c.cep || c.zipCode || ""));
    setValueInd("Endereco", c.address || "");
    setValueInd("Numero", onlyDigits(c.addressNumber || ""));
    setValueInd("Complemento", c.addressComplement || "");
    setValueInd("Bairro", c.addressNeighborhood || "");
    setValueInd("Cidade", c.addressCity || "");
    setValueInd("Cnae", onlyDigits(c.cnae || ""));
    if (c.openingDate) {
      const d = new Date(c.openingDate);
      if (!Number.isNaN(d.getTime())) {
        setValueInd("DataAbertura", String(d.toISOString()).slice(0, 10));
      }
    }
  }, [user, setValueInd]);

  // Chama o endpoint para verificar se já existe no indireto
  React.useEffect(() => {
    let mounted = true;
    async function run() {
      try {
        if (!user?.Company?.document) {
          setCheckingIndireto(false);
          return;
        }
        setCheckingIndireto(true);
        setIndiretoError("");

        const { data } = await axios.get(NEGATIVE.SEARCH_CUSTOMER);

        const exists = data?.cnpj; // adapte à resposta real

        if (!mounted) return;

        setShowIndiretoModal(!exists);
      } catch (e) {
        setShowIndiretoModal(true);
        setIndiretoError(
          e?.response?.data?.error ||
            e?.message ||
            "Não foi possível verificar cadastro indireto."
        );
      } finally {
        if (mounted) setCheckingIndireto(false);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [user]);

  // ===== Mutation: atualiza Company e chama backend para mandar ao EXTERNAL =====
  const registerIndiretoMutation = useMutation(async (values) => {
    const companyPayload = {
      name: (values.NomeFantasia || values.Nome || "").trim(),
      socialReason: (values.Nome || "").trim(),
      email: (values.EmailIndireto || "").trim(),
      typeCustomer: "CNPJ",
      document: onlyDigits(values.CnpjIndireto || values.Cnpj),
      phoneNumber: onlyDigits(values.Telefone),
      cep: onlyDigits(values.Cep),
      address: (values.Endereco || "").trim(),
      addressNumber: (values.Numero || "").trim(),
      addressComplement: (values.Complemento || "").trim() || null,
      addressNeighborhood: (values.Bairro || "").trim(),
      addressCity: (values.Cidade || "").trim(),
    };

    const companyId = user?.Company?.id;
    if (!companyId) throw new Error("Company id não encontrado.");

    await axios.put(COMPANY.COMPANY, companyPayload, { timeout: 25000 });

    const dadosClienteIndireto = {
      CnpjIndireto: onlyDigits(values.CnpjIndireto || values.Cnpj),
      EmailIndireto: String(values.EmailIndireto || "").trim(),
      NomePrincipal: String(values.Nome || "").trim(),
      NomeFantasia: "",
      Telefone: onlyDigits(values.Telefone),
      Cep: onlyDigits(values.Cep),
      Endereco: String(values.Endereco || "").trim(),
      Numero: String(values.Numero || "").trim(),
      Complemento: values.Complemento
        ? String(values.Complemento).trim()
        : undefined,
      Bairro: String(values.Bairro || "").trim(),
      Cidade: String(values.Cidade || "").trim(),
    };

    const { data } = await axios.post(
      NEGATIVE.CREATE_CUSTOMER_NEGATIVE,
      dadosClienteIndireto
    );

    return data;
  });

  const onSubmitIndireto = async (values) => {
    try {
      await registerIndiretoMutation.mutateAsync(values);
      setShowIndiretoModal(false);
      setIndiretoError("");
    } catch (e) {
      setIndiretoError(
        e?.response?.data?.error ||
          e?.message ||
          "Falha ao cadastrar cliente indireto (external)."
      );
    }
  };

  /* ====== Form principal: Negativação ====== */
  const [successMsg, setSuccessMsg] = React.useState("");
  const [cepLoading, setCepLoading] = React.useState(false);
  const [cepError, setCepError] = React.useState("");

  // estados para lookup de CNPJ (OpenCNPJ)
  const [cnpjLookupLoading, setCnpjLookupLoading] = React.useState(false);
  const [cnpjLookupError, setCnpjLookupError] = React.useState("");

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    shouldFocusError: true,
    defaultValues: {
      companyId: "",
      cnpj: "",
      cnpjClienteIndireto: "",
      documentoDevedor: "",
      codigoNatureza: "",
      dataVencimento: vencMax,
      valor: "",
      contrato: "",
      // Cheque / DC(22)
      numeroBanco: "",
      numeroAgencia: "",
      numeroCheque: "",
      numeroAlinea: "",
      numeroContaCorrente: "",
      // Pessoa
      dataNascimento: "",
      nome: "",
      nomePai: "",
      nomeMae: "",
      // Endereço/contato
      endereco: "",
      bairro: "",
      cidade: "",
      cep: "",
      uf: "",
      ddd: "",
      telefone: "",
      telefoneFull: "",
      email: "",
      // Cobrança
      usarCobranca: false,
      tipoCobranca: "",
      valorJurosMulta: "0",
      percentualDesconto: "0",
      // externos (opcionais)
      _externalSend: false,
      _externalUser: "",
      _externalPass: "",
      _externalUrl: "",
    },
  });

  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);

  const docWatch = useWatch({ control, name: "documentoDevedor" });
  const isCPFValido = React.useMemo(() => {
    const d = onlyDigits(docWatch);
    return !!d && d.length <= 11 && isValidCPF(d);
  }, [docWatch]);

  // observar natureza p/ regras de cheque e cobrança p/ UI
  const natureza = useWatch({ control, name: "codigoNatureza" });
  const isDC = String(natureza || "") === NATUREZA_DC;
  const usarCobrancaWatch = useWatch({ control, name: "usarCobranca" });

  React.useEffect(() => {
    if (user?.Company?.document) setValue("cnpj", user.Company.document);
  }, [user, setValue]);

  // ===== Auto-lookup OpenCNPJ quando documento é CNPJ válido =====
  React.useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      const raw = onlyDigits(docWatch);
      setCnpjLookupError("");
      if (!raw || raw.length <= 11) return; // só tenta para CNPJ
      if (!isValidCNPJ(raw)) return;
      try {
        setCnpjLookupLoading(true);
        const res = await fetch(`https://api.opencnpj.org/${raw}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Falha ao consultar OpenCNPJ");
        const data = await res.json();

        // Preenchimento "se vazio"
        const currentNome = getValues("nome");
        if (!currentNome) {
          const composedNome = (data?.razao_social || data?.nome_fantasia || "")
            .toString()
            .trim();
          if (composedNome) setValue("nome", composedNome.slice(0, 70));
        }

        const currentEndereco = getValues("endereco");
        if (!currentEndereco) {
          const baseEnd = [data?.logradouro, data?.numero, data?.complemento]
            .filter(Boolean)
            .join(", ");
          if (baseEnd) setValue("endereco", String(baseEnd).slice(0, 45));
        }

        const currentBairro = getValues("bairro");
        if (!currentBairro && data?.bairro) {
          setValue("bairro", String(data.bairro).slice(0, 20));
        }

        const currentCidade = getValues("cidade");
        if (!currentCidade && data?.municipio) {
          setValue("cidade", String(data.municipio).slice(0, 25));
        }

        const currentCEP = getValues("cep");
        if (!currentCEP && data?.cep) {
          setValue("cep", maskCEP(String(data.cep)));
        }

        const currentUF = getValues("uf");
        if (!currentUF && data?.uf) {
          setValue("uf", maskUF(String(data.uf)));
        }

        const currentEmail = getValues("email");
        if (!currentEmail && data?.email) {
          setValue("email", String(data.email).trim());
        }

        const currentDDD = getValues("ddd");
        const currentTel = getValues("telefone");
        if (
          (!currentDDD || !currentTel) &&
          Array.isArray(data?.telefones) &&
          data.telefones.length
        ) {
          const t = data.telefones[0];
          const ddd = onlyDigits(t.ddd || "").slice(0, 2);
          const numero = onlyDigits(t.numero || "").slice(0, 9);
          if (ddd && numero) {
            setValue("ddd", ddd);
            setValue("telefone", numero);
            setValue("telefoneFull", maskPhoneWithDDD(`${ddd}${numero}`));
          }
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          setCnpjLookupError(
            e?.message || "Erro ao consultar dados do CNPJ (OpenCNPJ)."
          );
        }
      } finally {
        setCnpjLookupLoading(false);
      }
    };
    run();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docWatch, setValue, getValues]);

  const mutation = useMutation(
    async (payload) => {
      const { data } = await axios.post(NEGATIVE.CREATE, payload);
      return data;
    },
    {
      onSuccess: () => {
        setSuccessMsg("Negativação criada com sucesso!");
        setTimeout(() => navigate("/negatives"), 900);
      },
    }
  );

  const onSubmit = async (values) => {
    try {
      // contrato uppercase e regex
      const contratoUpper = String(values.contrato || "")
        .toUpperCase()
        .trim();
      if (!CONTRATO_ALLOWED.test(contratoUpper)) {
        throw new Error(
          "Contrato inválido: use até 16 caracteres 0-9 A-Z / - ."
        );
      }

      // natureza DC => cheques obrigatórios; senão, zera
      let {
        numeroBanco,
        numeroAgencia,
        numeroCheque,
        numeroAlinea,
        numeroContaCorrente,
      } = values;

      if (String(values.codigoNatureza || "") === NATUREZA_DC) {
        numeroAgencia = onlyDigits(numeroAgencia).slice(0, 4);
        numeroCheque = onlyDigits(numeroCheque).slice(0, 4);
        numeroContaCorrente = onlyDigits(numeroContaCorrente).slice(0, 9);
      } else {
        numeroBanco = "";
        numeroAgencia = "";
        numeroCheque = "";
        numeroAlinea = "";
        numeroContaCorrente = "";
      }

      // valor com 2 casas
      const val = parseBRLToNumber(values.valor);
      const valor2 = Math.round(val * 100) / 100;

      const payload = {
        ...values,
        contrato: contratoUpper,
        cnpj: onlyDigits(values.cnpj),
        cnpjClienteIndireto: onlyDigits(values.cnpjClienteIndireto),
        documentoDevedor: onlyDigits(values.documentoDevedor),
        cep: onlyDigits(values.cep),
        ddd: onlyDigits(values.ddd),
        telefone: onlyDigits(values.telefone),
        numeroBanco: String(numeroBanco || "").slice(0, 4),
        numeroAgencia,
        numeroCheque,
        numeroAlinea: String(values.numeroAlinea || "").trim(),
        numeroContaCorrente,
        valor: valor2,
        tipoCobranca: String(values.tipoCobranca || "").toUpperCase(),
      };

      await mutation.mutateAsync(payload);
    } catch (error) {
      // Alert de erro já cobre
    }
  };

  const handleCepBlur = async () => {
    const cepDigits = onlyDigits(getValues("cep") || "");
    setCepError("");
    if (cepDigits.length !== 8) {
      if (cepDigits.length > 0) setCepError("CEP deve ter 8 dígitos.");
      return;
    }
    try {
      setCepLoading(true);
      const data = await lookupCEP(cepDigits);
      const endereco = getValues("endereco");
      const bairro = getValues("bairro");
      const cidade = getValues("cidade");
      const uf = getValues("uf");
      if (!endereco) setValue("endereco", data.logradouro || "");
      if (!bairro) setValue("bairro", data.bairro || "");
      if (!cidade) setValue("cidade", data.localidade || "");
      if (!uf) setValue("uf", data.uf || "");
    } catch (e) {
      setCepError(e?.message || "Erro ao consultar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const isSubmittingAny =
    isSubmitting || mutation.isLoading || registerIndiretoMutation.isLoading;

  const onCancel = () => {
    if (isSubmittingAny) return;
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      navigate(-1);
    }
  };

  if (!user?.Company?.hasCV) {
    return (
      <div className="relative">
        <ForbiddenQueriesPermission />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* MODAL: Cadastro Indireto (bloqueante até concluir) */}
      <Modal
        show={showIndiretoModal}
        onClose={() => {
          navigate(-1);
        }}
        size="5xl"
      >
        <Modal.Header>Atualize suas informações</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600 mb-4">
            Para continuar, precisamos concluir o cadastro junto ao provedor.
          </p>

          <form onSubmit={handleSubmitInd(onSubmitIndireto)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="EmailIndireto"
                control={controlInd}
                rules={{
                  required: "Email é obrigatório.",
                  validate: (s) =>
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) || "Email inválido.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Email *</Label>
                    <TextInput
                      type="email"
                      {...field}
                      color={errorsInd.EmailIndireto ? "failure" : undefined}
                      helperText={errorsInd.EmailIndireto?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name="CnpjIndireto"
                control={controlInd}
                rules={{
                  required: "CNPJ Indireto é obrigatório.",
                  validate: (v) =>
                    isValidCNPJ(v) ||
                    isValidCNPJ(onlyDigits(v)) ||
                    "CNPJ inválido",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">CNPJ Indireto *</Label>
                    <TextInput
                      value={maskCNPJ(field.value)}
                      onChange={(e) => field.onChange(e.target.value)}
                      color={errorsInd.CnpjIndireto ? "failure" : undefined}
                      helperText={errorsInd.CnpjIndireto?.message}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                )}
              />

              <Controller
                name="Nome"
                control={controlInd}
                rules={{ required: "Nome/Razão Social é obrigatório." }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Razão Social *</Label>
                    <TextInput
                      {...field}
                      color={errorsInd.Nome ? "failure" : undefined}
                      helperText={errorsInd.Nome?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name="Telefone"
                control={controlInd}
                rules={{
                  required: "Telefone é obrigatório.",
                  validate: (v) =>
                    onlyDigits(v).length >= 10 ||
                    "Informe DDD + telefone (10-11 dígitos).",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Telefone *</Label>
                    <TextInput
                      value={maskPhoneWithDDD(field.value)}
                      onChange={(e) => field.onChange(e.target.value)}
                      color={errorsInd.Telefone ? "failure" : undefined}
                      helperText={errorsInd.Telefone?.message}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 mt-4 gap-4 mb-4">
              <Controller
                name="Cep"
                control={controlInd}
                rules={{
                  required: "CEP é obrigatório.",
                  validate: (v) =>
                    onlyDigits(v).length === 8 || "CEP deve ter 8 dígitos.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">CEP *</Label>
                    <TextInput
                      value={maskCEP(field.value)}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="__.___-___"
                      color={errorsInd.Cep ? "failure" : undefined}
                      helperText={errorsInd.Cep?.message}
                      onBlur={async () => {
                        const cepDigits = onlyDigits(field.value);
                        if (cepDigits.length === 8) {
                          try {
                            const data = await lookupCEP(cepDigits);
                            if (!controlInd._formValues.Endereco)
                              setValueInd("Endereco", data.logradouro || "");
                            if (!controlInd._formValues.Bairro)
                              setValueInd("Bairro", data.bairro || "");
                            if (!controlInd._formValues.Cidade)
                              setValueInd("Cidade", data.localidade || "");
                          } catch {}
                        }
                      }}
                    />
                  </div>
                )}
              />

              <Controller
                name="Endereco"
                control={controlInd}
                rules={{ required: "Endereço é obrigatório." }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Endereço *</Label>
                    <TextInput
                      {...field}
                      color={errorsInd.Endereco ? "failure" : undefined}
                      helperText={errorsInd.Endereco?.message}
                      placeholder="Rua/Av"
                    />
                  </div>
                )}
              />

              <Controller
                name="Numero"
                control={controlInd}
                rules={{ required: "Número é obrigatório." }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Número *</Label>
                    <TextInput
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(onlyDigits(e.target.value))
                      }
                      color={errorsInd.Numero ? "failure" : undefined}
                      helperText={errorsInd.Numero?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name="Complemento"
                control={controlInd}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Complemento</Label>
                    <TextInput {...field} />
                  </div>
                )}
              />

              <Controller
                name="Bairro"
                control={controlInd}
                rules={{ required: "Bairro é obrigatório." }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Bairro *</Label>
                    <TextInput
                      {...field}
                      color={errorsInd.Bairro ? "failure" : undefined}
                      helperText={errorsInd.Bairro?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name="Cidade"
                control={controlInd}
                rules={{ required: "Cidade é obrigatória." }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Cidade *</Label>
                    <TextInput
                      {...field}
                      color={errorsInd.Cidade ? "failure" : undefined}
                      helperText={errorsInd.Cidade?.message}
                    />
                  </div>
                )}
              />
            </div>

            <div className="md:col-span-3 flex items-center justify-center gap-2 mt-2">
              <Button
                onClick={handleSubmitInd(onSubmitIndireto)}
                isProcessing={
                  isSubmittingInd || registerIndiretoMutation.isLoading
                }
              >
                Confirmar cadastro
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              Nova Negativação
            </h1>
            <p className="text-sm text-gray-500">
              Inclua uma nova negativação preenchendo os campos abaixo.
            </p>
          </div>
          <HiInformationCircle className="text-gray-400 w-6 h-6" />
        </div>
        <div className="relative overflow-hidden rounded-lg">
          {checkingIndireto && <DrawerLoading />}

          {/* Seção: Devedor & Título */}
          <Card className="shadow-sm border-0 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <HiLightningBolt className="text-indigo-500" />
              <h2 className="text-lg font-semibold">Dados principais</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="field-cnpj" className="mb-1 block">
                  CNPJ Credor
                </Label>
                <div className="mt-1 text-gray-600">
                  {formatDocument(user?.Company?.document)}
                </div>
              </div>
              <div>
                <Label htmlFor="field-razao" className="mb-1 block">
                  Razão Social
                </Label>
                <div className="mt-1 text-gray-600">
                  {user?.Company?.socialReason || user?.Company?.name}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Documento Devedor */}
              <Controller
                name="documentoDevedor"
                control={control}
                rules={{
                  required: "Documento do devedor é obrigatório.",
                  validate: (value) => {
                    const d = onlyDigits(value);
                    if (!d) return "Informe CPF ou CNPJ.";
                    if (d.length <= 11) return isValidCPF(d) || "CPF inválido";
                    return isValidCNPJ(d) || "CNPJ inválido";
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label
                      htmlFor="field-documentoDevedor"
                      className="mb-1 block"
                    >
                      Documento Devedor *
                    </Label>
                    <TextInput
                      id="field-documentoDevedor"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(maskCNPJorCPF(e.target.value))
                      }
                      placeholder="CPF/CNPJ"
                      color={
                        errors.documentoDevedor || cnpjLookupError
                          ? "failure"
                          : cnpjLookupLoading
                          ? "info"
                          : undefined
                      }
                      helperText={
                        (
                          <span className="text-xs block -mt-1">
                            {errors.documentoDevedor?.message}
                          </span>
                        ) ||
                        (cnpjLookupLoading ? (
                          <span className="text-xs block -mt-1">
                            Consultando dados do CNPJ...
                          </span>
                        ) : (
                          cnpjLookupError || ""
                        ))
                      }
                      required
                    />
                  </div>
                )}
              />

              {/* Nome Principal */}
              <Controller
                name="nome"
                control={control}
                rules={{
                  required: "Nome do devedor é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 70) || "Máx. 70 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-nome" className="mb-1 block">
                      Nome/Razão Social do Devedor *
                    </Label>
                    <TextInput
                      id="field-nome"
                      {...field}
                      placeholder="Nome/Razão Social"
                      maxLength={70}
                    />
                    {errors.nome?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.nome.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Natureza */}
              <Controller
                name="codigoNatureza"
                control={control}
                rules={{
                  required: "Código natureza é obrigatório.",
                  validate: (v) =>
                    (String(v || "").length > 0 && String(v).length <= 2) ||
                    "Máx. 2 caracteres.",
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-codnat" className="mb-1 block">
                      Natureza *
                    </Label>
                    <Select
                      id="field-codnat"
                      value={String(field.value || "")}
                      onChange={(e) => field.onChange(e.target.value)}
                      color={errors.codigoNatureza ? "failure" : undefined}
                      required
                    >
                      <option value="">Selecione...</option>
                      {NATUREZAS.map((n) => (
                        <option key={n.Codigo} value={String(n.Codigo)}>
                          {n.Sigla} — {n.Descricao} (cód. {n.Codigo})
                        </option>
                      ))}
                    </Select>
                    {errors.codigoNatureza?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.codigoNatureza.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Vencimento */}
              <Controller
                name="dataVencimento"
                control={control}
                rules={{
                  required: "Vencimento é obrigatório.",
                  validate: (v) => {
                    if (!v) return "Vencimento é obrigatório.";
                    const d = new Date(v);
                    if (Number.isNaN(d.getTime())) return "Data inválida.";
                    const min = new Date(vencMin);
                    const max = new Date(vencMax);
                    if (d < min || d > max) {
                      const fmt = (s) => {
                        const [yy, mm, dd] = s.split("-");
                        return `${dd}/${mm}/${yy}`;
                      };
                      return `A data do vencimento deve ser não superior a 4 anos e 11 meses (mín: ${fmt(
                        vencMin
                      )}) e inferior a 4 dias (máx: ${fmt(
                        vencMax
                      )}) em relação à data de hoje.`;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label
                      htmlFor="field-dataVencimento"
                      className="mb-1 block"
                    >
                      Data Vencimento *
                    </Label>
                    <TextInput
                      id="field-dataVencimento"
                      type="date"
                      value={field.value}
                      onChange={field.onChange}
                      min={vencMin}
                      max={vencMax}
                      color={errors.dataVencimento ? "failure" : undefined}
                      helperText={errors.dataVencimento?.message}
                      required
                    />
                  </div>
                )}
              />

              {/* Valor */}
              <Controller
                name="valor"
                control={control}
                rules={{
                  required: "Valor é obrigatório.",
                  validate: (v) => {
                    const n = parseBRLToNumber(v);
                    if (!Number.isFinite(n) || n <= 0)
                      return "Informe um valor válido.";
                    return true;
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-valor" className="mb-1 block">
                      Valor *
                    </Label>
                    <TextInput
                      id="field-valor"
                      type="text"
                      inputMode="numeric"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(formatBRLMask(e.target.value))
                      }
                      placeholder="0,00"
                      color={errors.valor ? "failure" : undefined}
                      helperText={errors.valor?.message}
                      required
                    />
                  </div>
                )}
              />

              {/* Contrato */}
              <Controller
                name="contrato"
                control={control}
                rules={{
                  required: "Contrato é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "")
                      .toUpperCase()
                      .trim();
                    return (
                      CONTRATO_ALLOWED.test(s) ||
                      "Use até 16 caracteres: 0-9 A-Z / - ."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label htmlFor="field-contrato" className="mb-1 block">
                      Nº Contrato *
                    </Label>
                    <TextInput
                      id="field-contrato"
                      {...field}
                      maxLength={16}
                      color={errors.contrato ? "failure" : undefined}
                      helperText={
                        <span className="block text-xs -mt-1">
                          {errors.contrato?.message}
                        </span>
                      }
                      required
                    />
                    <span className="text-2xs italic text-gray-500 block leadin-none mt-0.5">
                      Número de Contrato, Nota Fiscal, Boletos, Duplicatas, etc.
                    </span>
                  </div>
                )}
              />
            </div>
          </Card>

          {/* Endereço & Contato + Pessoa */}
          <Card className="shadow-sm border-0 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <HiLightningBolt className="text-indigo-500" />
              <h2 className="text-lg font-semibold">
                Endereço, Contato & Pessoa
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Pessoa (só aparece se CPF válido) */}
              {isCPFValido && (
                <>
                  <Controller
                    name="dataNascimento"
                    control={control}
                    shouldUnregister
                    rules={{
                      required: isCPFValido
                        ? "Data de nascimento é obrigatória para CPF."
                        : false,
                      validate: (v) =>
                        isAdult(v) || "Devedor deve ter 18 anos ou mais.",
                    }}
                    render={({ field }) => (
                      <div>
                        <Label className="mb-1 block">
                          Data de Nascimento *
                        </Label>
                        <TextInput
                          type="date"
                          {...field}
                          required={isCPFValido}
                        />
                        {errors.dataNascimento?.message && (
                          <div className="text-xs text-red-600 mt-1">
                            {errors.dataNascimento.message}
                          </div>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    name="nomePai"
                    control={control}
                    shouldUnregister
                    rules={{
                      validate: (v) =>
                        String(v || "").trim().length <= 70 ||
                        "Máx. 70 caracteres.",
                    }}
                    render={({ field }) => (
                      <div>
                        <Label className="mb-1 block">Nome do Pai</Label>
                        <TextInput {...field} maxLength={70} />
                        {errors.nomePai?.message && (
                          <div className="text-xs text-red-600 mt-1">
                            {errors.nomePai.message}
                          </div>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    name="nomeMae"
                    control={control}
                    shouldUnregister
                    rules={{
                      validate: (v) =>
                        String(v || "").trim().length <= 70 ||
                        "Máx. 70 caracteres.",
                    }}
                    render={({ field }) => (
                      <div>
                        <Label className="mb-1 block">Nome da Mãe</Label>
                        <TextInput {...field} maxLength={70} />
                        {errors.nomeMae?.message && (
                          <div className="text-xs text-red-600 mt-1">
                            {errors.nomeMae.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </>
              )}

              {/* CEP */}
              <Controller
                name="cep"
                control={control}
                rules={{
                  required: "CEP é obrigatório.",
                  validate: (v) =>
                    onlyDigits(v).length === 8 || "CEP deve ter 8 dígitos.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">CEP *</Label>
                    <TextInput
                      id="field-cep"
                      value={field.value}
                      onChange={(e) => field.onChange(maskCEP(e.target.value))}
                      onBlur={async () => {
                        field.onBlur();
                        await handleCepBlur();
                      }}
                      placeholder="__.___-___"
                      color={cepError ? "failure" : null}
                      helperText={
                        cepError ? (
                          <span className="text-xs -mt-1 block">
                            CEP inválido
                          </span>
                        ) : cepLoading ? (
                          <span className="text-xs -mt-1 block">
                            Consultando CEP...
                          </span>
                        ) : field.value &&
                          onlyDigits(field.value).length === 8 ? (
                          <span className="text-xs -mt-1 block">
                            CEP Válido
                          </span>
                        ) : (
                          ""
                        )
                      }
                      required
                      maxLength={10}
                    />
                  </div>
                )}
              />

              {/* Endereco */}
              <Controller
                name="endereco"
                control={control}
                rules={{
                  required: "Endereço é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 45) || "Máx. 45 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Endereço *</Label>
                    <TextInput
                      {...field}
                      placeholder="Rua/Av, número e complemento"
                      required
                      maxLength={45}
                    />
                    {errors.endereco?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.endereco.message}
                      </div>
                    )}
                  </div>
                )}
              />

              <Controller
                name="numero"
                control={control}
                rules={{
                  required: "Número é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 20) || "Máx. 20 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Número *</Label>
                    <TextInput {...field} required maxLength={20} />
                    {errors.numero?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.numero.message}
                      </div>
                    )}
                  </div>
                )}
              />

              <Controller
                name="complemento"
                control={control}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Complemento</Label>
                    <TextInput {...field} required maxLength={20} />
                  </div>
                )}
              />

              {/* Bairro */}
              <Controller
                name="bairro"
                control={control}
                rules={{
                  required: "Bairro é obrigatório.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 20) || "Máx. 20 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Bairro *</Label>
                    <TextInput {...field} required maxLength={20} />
                    {errors.bairro?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.bairro.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Cidade */}
              <Controller
                name="cidade"
                control={control}
                rules={{
                  required: "Cidade é obrigatória.",
                  validate: (v) => {
                    const s = String(v || "").trim();
                    return (
                      (s.length > 0 && s.length <= 25) || "Máx. 25 caracteres."
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Cidade *</Label>
                    <TextInput {...field} required maxLength={25} />
                    {errors.cidade?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.cidade.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* UF como Select */}
              <Controller
                name="uf"
                control={control}
                rules={{
                  required: "UF é obrigatória.",
                  validate: (v) =>
                    !v || v.length === 2 || "UF deve ter 2 letras.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">UF *</Label>
                    <Select
                      value={String(field.value || "")}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                      color={errors.uf ? "failure" : undefined}
                      required
                    >
                      <option value="">Selecione...</option>
                      {UFS.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </Select>
                    {errors.uf?.message && (
                      <div className="text-xs text-red-600 mt-1">
                        {errors.uf.message}
                      </div>
                    )}
                  </div>
                )}
              />

              {/* Telefone com DDD */}
              <Controller
                name="telefoneFull"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return true;
                    const d = onlyDigits(value);
                    const len = d.length;
                    return (
                      (len >= 10 && len <= 11) ||
                      "Informe DDD + telefone (10 a 11 dígitos)"
                    );
                  },
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Telefone (com DDD)</Label>
                    <TextInput
                      value={field.value}
                      onChange={(e) => {
                        const masked = maskPhoneWithDDD(e.target.value);
                        field.onChange(masked);
                        const { ddd, telefone } = splitDDDPhone(masked);
                        setValue("ddd", ddd, { shouldValidate: true });
                        setValue("telefone", telefone, {
                          shouldValidate: true,
                        });
                      }}
                      placeholder="(11) 99999-9999"
                      color={errors.telefoneFull ? "failure" : undefined}
                      helperText={errors.telefoneFull?.message}
                    />
                    <span className="text-2xs italic text-gray-500 block leadin-none mt-0.5">
                      Será encaminhado SMS com uma notificação
                    </span>
                  </div>
                )}
              />

              {/* Email */}
              <Controller
                name="email"
                control={control}
                rules={{
                  setValueAs: (v) => (v ?? "").trim(),
                  validate: (s) =>
                    !s ||
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ||
                    "Email inválido.",
                }}
                render={({ field }) => (
                  <div>
                    <Label className="mb-1 block">Email</Label>
                    <TextInput
                      type="email"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="email@email.com"
                      color={errors.email ? "failure" : undefined}
                      helperText={errors.email?.message}
                    />
                    <span className="text-2xs italic text-gray-500 block leadin-none mt-0.5">
                      Será encaminhado E-mail com uma notificação
                    </span>
                  </div>
                )}
              />
            </div>
          </Card>

          {/* Cobrança & Cheque */}
          <Card className="shadow-sm border-0 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <HiLightningBolt className="text-indigo-500" />
              <h2 className="text-lg font-semibold">Cobrança</h2>
            </div>

            <Controller
              name="usarCobranca"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    id="ck-usar-cobranca"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <Label htmlFor="ck-usar-cobranca">
                    Usar Serviço de Cobrança
                  </Label>
                </div>
              )}
            />

            {watch("usarCobranca") && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                  name="tipoCobranca"
                  control={control}
                  rules={{
                    validate: (v) =>
                      !usarCobrancaWatch ||
                      !v ||
                      ["S", "E"].includes(String(v).toUpperCase()) ||
                      'Use "S" (Simples) ou "E" (Extra-Judicial).',
                  }}
                  render={({ field }) => (
                    <div>
                      <Label className="mb-1 block">Tipo Cobrança</Label>
                      <Select
                        {...field}
                        value={String(field.value || "")}
                        onChange={(e) => field.onChange(e.target.value)}
                        disabled={!usarCobrancaWatch}
                      >
                        <option value="">Selecione...</option>
                        <option value="S">Cobrança Simples</option>
                        <option value="E">Cobrança Extra-Judicial</option>
                      </Select>
                    </div>
                  )}
                />

                <Controller
                  name="valorJurosMulta"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Label className="mb-1 block">
                        Valor Juros/Multa de acréscimo
                      </Label>
                      <TextInput type="number" step="0.01" {...field} />
                    </div>
                  )}
                />

                <Controller
                  name="percentualDesconto"
                  control={control}
                  rules={{
                    validate: (v) =>
                      Number(v || 0) <= 100 ||
                      "Percentual não pode passar de 100%.",
                  }}
                  render={({ field }) => (
                    <div>
                      <Label className="mb-1 block">
                        % de Desconto na Negociação
                      </Label>
                      <TextInput
                        id="field-percentualDesconto"
                        type="number"
                        step="0.01"
                        {...field}
                      />
                      {errors.percentualDesconto?.message && (
                        <div className="text-xs text-red-600 mt-1">
                          {errors.percentualDesconto.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
          </Card>
        </div>

        {successMsg && (
          <Alert color="success" className="mt-1" icon={HiCheckCircle}>
            {successMsg}
          </Alert>
        )}
        {mutation.isError && (
          <Alert color="failure" className="mt-1" icon={HiInformationCircle}>
            {mutation.error?.response?.data?.error ||
              mutation.error?.message ||
              "Erro ao criar negativação"}
          </Alert>
        )}

        {/* Barra de ações */}
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-center mt-4">
          <div className="flex items-center gap-2">
            <Button
              className="px-4"
              color="light"
              onClick={onCancel}
              disabled={isSubmittingAny}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              isProcessing={isSubmittingAny}
              disabled={showIndiretoModal}
            >
              Criar negativação
            </Button>
          </div>
        </div>
      </div>

      {/* Modal de confirmação de cancelamento */}
      <Modal
        show={showCancelConfirm}
        size="md"
        onClose={() => setShowCancelConfirm(false)}
      >
        <Modal.Header>Descartar alterações?</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Você inseriu dados no formulário. Se sair agora, as mudanças serão
            perdidas.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button color="light" onClick={() => setShowCancelConfirm(false)}>
              Continuar editando
            </Button>
            <Button
              color="failure"
              onClick={() => {
                setShowCancelConfirm(false);
                navigate(-1);
              }}
            >
              Descartar alterações
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
